import { buildColumnTypeMap, cellTypeForColumn } from '../../reporting-core/column-types';
import { ReportCellType } from '../../reporting-core/cell-format';
import { coloredText, escapeHtml, link, renderStandardCell, ReportRenderConfig } from '../../reporting-core/cell-html';
import { getEventDetailsLink } from '../../reporting-core/utils';
import { Booking } from '../booking';
import { getContentItem } from '../exports/bookings';
import {
  COLUMN_NAME_BOOKING_ID,
  COLUMN_NAME_CREATED_DATE,
  COLUMN_NAME_EVENT_ID,
  COLUMN_NAME_EVENT_NAME,
  COLUMN_NAME_EVENT_LOCATION,
  COLUMN_NAME_EVENT_START_DATE,
  COLUMN_NAME_EVENT_SCHEDULE_TYPE,
  COLUMN_NAME_EVENT_TYPE_STATUS,
  COLUMN_NAME_NUMBER_OF_PARTICIPANTS,
  COLUMN_NAME_PURCHASE_PRICE,
  COLUMN_NAME_PURCHASE_TAX_AMOUNT,
  COLUMN_NAME_AMOUNT_PAID,
  COLUMN_NAME_AMOUNT_DUE,
  COLUMN_NAME_AMOUNT_REFUNDED,
  COLUMN_NAME_NUMBER_OF_VOUCHERS_USED,
  COLUMN_NAME_VOUCHERS_AMOUNT_PAID,
  COLUMN_NAME_INVOICE_ID,
  COLUMN_NAME_BOOKING_CUSTOMER_ID,
  COLUMN_NAME_BOOKING_CUSTOMER_FIRST_NAME,
  COLUMN_NAME_BOOKING_CUSTOMER_LAST_NAME,
  COLUMN_NAME_BOOKING_CUSTOMER_BIRTHDAY,
  COLUMN_NAME_BOOKING_CUSTOMER_EMAIL,
  COLUMN_NAME_PAYMENT_METHOD,
  COLUMN_NAME_BOOKING_IS_CANCELLED,
  COLUMN_NAME_PRICING_MATRIX_ID,
  COLUMN_NAME_PRICING_MATRIX_NAME,
  COLUMN_NAME_LINKED_PRIVATE_EVENT_ID,
  COLUMN_NAME_LINKED_PRIVATE_EVENT_NAME,
} from '../exports/bookings'

export {
  COLUMN_NAME_BOOKING_ID,
  COLUMN_NAME_CREATED_DATE,
  COLUMN_NAME_EVENT_ID,
  COLUMN_NAME_EVENT_NAME,
  COLUMN_NAME_EVENT_LOCATION,
  COLUMN_NAME_EVENT_START_DATE,
  COLUMN_NAME_EVENT_SCHEDULE_TYPE,
  COLUMN_NAME_EVENT_TYPE_STATUS,
  COLUMN_NAME_NUMBER_OF_PARTICIPANTS,
  COLUMN_NAME_PURCHASE_PRICE,
  COLUMN_NAME_PURCHASE_TAX_AMOUNT,
  COLUMN_NAME_AMOUNT_PAID,
  COLUMN_NAME_AMOUNT_DUE,
  COLUMN_NAME_AMOUNT_REFUNDED,
  COLUMN_NAME_NUMBER_OF_VOUCHERS_USED,
  COLUMN_NAME_VOUCHERS_AMOUNT_PAID,
  COLUMN_NAME_INVOICE_ID,
  COLUMN_NAME_BOOKING_CUSTOMER_ID,
  COLUMN_NAME_BOOKING_CUSTOMER_FIRST_NAME,
  COLUMN_NAME_BOOKING_CUSTOMER_LAST_NAME,
  COLUMN_NAME_BOOKING_CUSTOMER_BIRTHDAY,
  COLUMN_NAME_BOOKING_CUSTOMER_EMAIL,
  COLUMN_NAME_PAYMENT_METHOD,
  COLUMN_NAME_BOOKING_IS_CANCELLED,
  COLUMN_NAME_PRICING_MATRIX_ID,
  COLUMN_NAME_PRICING_MATRIX_NAME,
  COLUMN_NAME_LINKED_PRIVATE_EVENT_ID,
  COLUMN_NAME_LINKED_PRIVATE_EVENT_NAME,
}

export const COLUMN_NAME_BOOKING_CUSTOMER = 'Booking Customer';

export const COLUMN_HEADER_OVERRIDES: {[key: string]: string} = {
  [COLUMN_NAME_PURCHASE_PRICE]: 'Purchase Price',
  [COLUMN_NAME_VOUCHERS_AMOUNT_PAID]: 'Amount Paid For Vouchers',
};

export const REPORT_COLUMN_NAMES = [
  COLUMN_NAME_CREATED_DATE,
  COLUMN_NAME_EVENT_NAME,
  COLUMN_NAME_EVENT_LOCATION,
  COLUMN_NAME_EVENT_START_DATE,
  COLUMN_NAME_NUMBER_OF_PARTICIPANTS,
  COLUMN_NAME_PURCHASE_PRICE,
  COLUMN_NAME_BOOKING_CUSTOMER,
  COLUMN_NAME_BOOKING_ID,
  COLUMN_NAME_EVENT_ID,
  COLUMN_NAME_EVENT_SCHEDULE_TYPE,
  COLUMN_NAME_EVENT_TYPE_STATUS,
  COLUMN_NAME_PURCHASE_TAX_AMOUNT,
  COLUMN_NAME_AMOUNT_PAID,
  COLUMN_NAME_AMOUNT_DUE,
  COLUMN_NAME_AMOUNT_REFUNDED,
  COLUMN_NAME_NUMBER_OF_VOUCHERS_USED,
  COLUMN_NAME_VOUCHERS_AMOUNT_PAID,
  COLUMN_NAME_INVOICE_ID,
  COLUMN_NAME_BOOKING_CUSTOMER_ID,
  COLUMN_NAME_BOOKING_CUSTOMER_FIRST_NAME,
  COLUMN_NAME_BOOKING_CUSTOMER_LAST_NAME,
  COLUMN_NAME_BOOKING_CUSTOMER_BIRTHDAY,
  COLUMN_NAME_BOOKING_CUSTOMER_EMAIL,
  COLUMN_NAME_PAYMENT_METHOD,
  COLUMN_NAME_BOOKING_IS_CANCELLED,
  COLUMN_NAME_PRICING_MATRIX_ID,
  COLUMN_NAME_PRICING_MATRIX_NAME,
  COLUMN_NAME_LINKED_PRIVATE_EVENT_ID,
  COLUMN_NAME_LINKED_PRIVATE_EVENT_NAME,
];

export const COLUMN_TYPES = buildColumnTypeMap({
  [ReportCellType.objectId]: [
    COLUMN_NAME_BOOKING_ID,
    COLUMN_NAME_EVENT_ID,
    COLUMN_NAME_INVOICE_ID,
    COLUMN_NAME_BOOKING_CUSTOMER_ID,
    COLUMN_NAME_PRICING_MATRIX_ID,
    COLUMN_NAME_LINKED_PRIVATE_EVENT_ID,
  ],
  [ReportCellType.currency]: [
    COLUMN_NAME_PURCHASE_PRICE,
    COLUMN_NAME_PURCHASE_TAX_AMOUNT,
    COLUMN_NAME_AMOUNT_PAID,
    COLUMN_NAME_AMOUNT_DUE,
    COLUMN_NAME_AMOUNT_REFUNDED,
    COLUMN_NAME_VOUCHERS_AMOUNT_PAID,
  ],
  [ReportCellType.date]: [
    COLUMN_NAME_EVENT_START_DATE,
    COLUMN_NAME_CREATED_DATE,
    COLUMN_NAME_BOOKING_CUSTOMER_BIRTHDAY,
  ],
  [ReportCellType.custom]: [
    COLUMN_NAME_EVENT_NAME,
    COLUMN_NAME_BOOKING_CUSTOMER,
  ],
});

/**
 * The Staff Site path to the details page of the event a booking is for, or
 * null when it cannot be addressed: the event has no parts, or its
 * denormalised start/end datetimes are unset (possible on events built from a
 * private-event request) — getEventDetailsLink would otherwise concatenate
 * them into a broken ".../past/null/..." path.
 *
 * Takes the fields structurally rather than a `Booking`, so the customer
 * profile's Bookings sub-list shares this guard instead of keeping its own
 * copy (PRO-1338).
 */
export function getBookingEventDetailsLink(booking: {
  event_first_part_id: number | null,
  event_start_datetime: string | null,
  event_end_datetime: string | null,
  event_start_date: string | null,
}): string | null {
  if (
    !booking.event_first_part_id ||
    !booking.event_start_datetime ||
    !booking.event_end_datetime ||
    !booking.event_start_date
  ) {
    return null;
  }

  return getEventDetailsLink(
    booking.event_start_datetime,
    booking.event_end_datetime,
    booking.event_start_date,
    booking.event_first_part_id,
  );
}

export function getDisplayText(booking: Booking, column: string, config: ReportRenderConfig): string {
  switch (column) {
    case COLUMN_NAME_EVENT_NAME: {
      const eventLink = getBookingEventDetailsLink(booking);
      return eventLink
        ? link(config, eventLink, escapeHtml(booking.event_type_name))
        : escapeHtml(booking.event_type_name);
    }
    case COLUMN_NAME_BOOKING_CUSTOMER:
      return booking.booking_customer_id
        // The Bookings view, not the Reservations one: the booker is often not
        // a participant, so their own reservations list need not contain this
        // booking at all (PRO-1338).
        ? link(config, '/customers/' + booking.booking_customer_id + '/reservations/bookings',
               escapeHtml(booking.booking_customer_first_name) + ' ' + escapeHtml(booking.booking_customer_last_name))
        : coloredText('text-muted', 'Not linked');
    default:
      return renderStandardCell(getContentItem(booking, column), cellTypeForColumn(COLUMN_TYPES, column), config);
  }
}
